import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import {
  useCloseLaborMonth,
  useLaborAllocations,
  useProjectPnl,
} from '../../hooks/useOps';
import { formatIDR } from '../../lib/format';

function currentMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

function Kpi({ label, value, accent }: { label: string; value: string; accent?: 'good' | 'bad' }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography
        variant="h6"
        sx={{
          fontVariantNumeric: 'tabular-nums',
          color: accent === 'good' ? 'success.main' : accent === 'bad' ? 'error.main' : undefined,
        }}
      >
        {value}
      </Typography>
    </Paper>
  );
}

/**
 * The Money tab — the per-project P&L the finance epic exists to answer:
 * revenue vs contract value, direct cost (tagged expenses), labor cost
 * (allocation engine off daily-logs), profit + margin, and a month-bucketed
 * cost timeline. Route-gated to canSeeCash (tab hidden otherwise).
 */
export function MoneyTab({ projectId }: { projectId: string }) {
  const { data: pnl, isLoading, error } = useProjectPnl(projectId);
  const [month] = useState(currentMonth());
  const { data: allocations } = useLaborAllocations(month);
  const closeMonth = useCloseLaborMonth();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !pnl) {
    return <Alert severity="error">{(error as Error)?.message ?? 'Failed to load P&L'}</Alert>;
  }

  const profitNegative = Number(pnl.profit) < 0;
  const marginPct = pnl.margin != null ? `${(Number(pnl.margin) * 100).toFixed(1)}%` : '—';
  const monthRows = (allocations?.rows ?? []).filter(
    (r) => r.projectId === projectId || r.projectId === null,
  );

  return (
    <Stack gap={2}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <Kpi label="Contract value" value={formatIDR(pnl.contractValue)} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <Kpi label="Invoiced" value={formatIDR(pnl.invoiced)} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <Kpi label="Paid" value={formatIDR(pnl.paid)} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <Kpi label="Remaining to bill" value={formatIDR(pnl.remaining)} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <Kpi label="Direct cost (expenses)" value={formatIDR(pnl.directCost)} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <Kpi label="Labor cost (allocated)" value={formatIDR(pnl.laborCost)} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <Kpi
            label="Profit"
            value={formatIDR(pnl.profit)}
            accent={profitNegative ? 'bad' : 'good'}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <Kpi label="Margin (on invoiced)" value={marginPct} accent={profitNegative ? 'bad' : 'good'} />
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Cost timeline
        </Typography>
        {pnl.timeline.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            No dated activity yet.
          </Typography>
        ) : (
          <BarChart
            height={280}
            xAxis={[{ data: pnl.timeline.map((b) => b.month), scaleType: 'band' }]}
            series={[
              {
                label: 'Direct cost',
                data: pnl.timeline.map((b) => Number(b.directCost)),
                stack: 'cost',
              },
              {
                label: 'Labor cost',
                data: pnl.timeline.map((b) => Number(b.laborCost)),
                stack: 'cost',
              },
            ]}
          />
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle2">
            Labor allocation — {month}{' '}
            {allocations?.closed ? (
              <Chip size="small" label="closed" color="success" variant="outlined" />
            ) : (
              <Chip size="small" label="live (not closed)" variant="outlined" />
            )}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => closeMonth.mutate(month)}
            disabled={closeMonth.isPending}
          >
            Close month
          </Button>
        </Stack>
        {monthRows.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            No allocations this month (people need a monthly cost + daily logs).
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Person</TableCell>
                <TableCell>Bucket</TableCell>
                <TableCell align="right">Days</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Source</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {monthRows.map((r, i) => (
                <TableRow key={`${r.personId}-${r.projectId ?? 'overhead'}-${i}`}>
                  <TableCell>{r.personName}</TableCell>
                  <TableCell>
                    {r.projectId === projectId ? (
                      'This project'
                    ) : (
                      <Chip size="small" label="Unallocated (overhead)" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right">{r.days}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatIDR(r.amount)}
                  </TableCell>
                  <TableCell>{r.source}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
}
